import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

declare var intlTelInput: any;

@Component({
  selector: 'app-phone-input',
  standalone: false,
  template: `
    <input 
      #phoneInput
      type="tel" 
      [placeholder]="placeholder"
      [class]="inputClass"
      [id]="inputId"
      [attr.maxlength]="maxlength"
      (input)="onInput($event)"
      (blur)="onBlur()"
      (focus)="onFocus()"
      (keypress)="onKeyPress($event)"
      (paste)="onPaste($event)"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ]
})
export class PhoneInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('phoneInput', { static: true }) phoneInputElement!: ElementRef;
  
  @Input() options: any = {};
  @Input() placeholder: string = '';
  @Input() inputClass: string = '';
  @Input() inputId: string = '';
  @Input() maxlength: number = 15;
  @Input() separateDialCode: boolean = true;
  
  @Output() countryChange = new EventEmitter<any>();
  @Output() intlTelInputObject = new EventEmitter<any>();
  
  private iti: any;
  private value: string = '';
  private onChange = (_value: string) => {};
  private onTouched = () => {};

  ngOnInit() {
    setTimeout(() => {
      this.initializeIntlTelInput();
    }, 100);
  }

  ngOnDestroy() {
    if (this.iti) {
      this.iti.destroy();
    }
  }

  private initializeIntlTelInput() {
    if (typeof intlTelInput !== 'undefined') {
      const defaultOptions = {
        separateDialCode: this.separateDialCode,
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.6/js/utils.js',
        ...this.options
      };

      this.iti = intlTelInput(this.phoneInputElement.nativeElement, defaultOptions);
      
      // Emit the intl tel input object
      this.intlTelInputObject.emit(this.iti);
      
      // Listen for country change
      this.phoneInputElement.nativeElement.addEventListener('countrychange', () => {
        const countryData = this.iti.getSelectedCountryData();
        this.countryChange.emit(countryData);
        
        // Also emit input change to update the form control with the new number
        const fullNumber = this.iti.getNumber();
        if (countryData && fullNumber) {
          const nationalNumber = fullNumber.replace('+' + countryData.dialCode, '');
          this.onChange(nationalNumber);
        }
      });
    }
  }

  onInput(event: any) {
    this.value = event.target.value;
    // Extract just the number part without the country code for the form control
    if (this.iti) {
      const countryData = this.iti.getSelectedCountryData();
      const fullNumber = this.iti.getNumber();
      if (countryData && fullNumber) {
        // Remove the country code from the full number to get just the national number
        const nationalNumber = fullNumber.replace('+' + countryData.dialCode, '');
        this.onChange(nationalNumber);
      } else {
        this.onChange(this.value);
      }
    } else {
      this.onChange(this.value);
    }
  }

  onBlur() {
    this.onTouched();
  }

  onFocus() {
    // Handle focus event if needed
  }

  onKeyPress(event: any) {
    // Allow only numeric input
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  onPaste(event: any) {
    // Handle paste event - remove spaces
    setTimeout(() => {
      const value = event.target.value.replace(/\s/g, '');
      event.target.value = value;
      this.value = value;
      this.onChange(this.value);
    }, 1);
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    if (this.phoneInputElement && this.phoneInputElement.nativeElement) {
      this.phoneInputElement.nativeElement.value = this.value;
      // If intl-tel-input is initialized, update it as well
      if (this.iti && this.value) {
        this.iti.setNumber(this.value);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.phoneInputElement) {
      this.phoneInputElement.nativeElement.disabled = isDisabled;
    }
  }

  // Public methods for external access
  setNumber(number: string) {
    if (this.iti) {
      this.iti.setNumber(number);
    }
  }

  setCountry(countryCode: string) {
    if (this.iti) {
      this.iti.setCountry(countryCode);
    }
  }

  getSelectedCountryData() {
    return this.iti ? this.iti.getSelectedCountryData() : null;
  }

  getNumber() {
    return this.iti ? this.iti.getNumber() : this.value;
  }

  getValidationError() {
    return this.iti ? this.iti.getValidationError() : null;
  }

  isValidNumber() {
    return this.iti ? this.iti.isValidNumber() : false;
  }
}